/** Sparse, deterministic drainage graph on the actual terrain, including
 * mountains and lake basins. Seeded sources trace downhill toward the sea;
 * closed basins and routes requiring deep excavation are discarded. Final
 * interpolated beds must fit a bounded channel cut. A spatial hash serves the
 * same water elevation and current to terrain, rendering and gameplay. */
import { mulberry32, hashSeed, range, type Rng } from "./rng.js";
import * as pmath from "./pmath.js";
import type { PlanetSampler, TerrainSample } from "./universe.js";
import { buildRiverFalls, fitNativeRiverCliffs, fallBed, fallInfluence, RIVER_MAX_CUT_M, HYDROLOGY_VERSION, type RiverFall } from './riverFalls.js';
import {buildRiverEscarpment,sampleRiverEscarpments,escarpmentSample,type RiverEscarpment} from './riverEscarpments.js';
import {canyonPoint,canyonLocation} from './canyons.js';
import {buildRiverPools,carveRiverPoolHeight,riverPoolInfluence,riverPoolWaterHalf,type RiverPoolDef,type RiverPoolTerrain} from './riverPools.js';
import {carveRiverLedgeHeight,carveWaterfallBankHeight} from './riverLedges.js';
export * from './riverFalls.js';

/** A point along a river centreline, source → mouth order. */
export interface RiverPoint {
  /** Unit direction on the sphere (unwarped). */
  x: number;
  y: number;
  z: number;
  /** Water-surface elevation, amplitude units (monotonically descending). */
  ws: number;
  /** Channel-bed elevation = ws − depth, amplitude units (terrain carves to this). */
  bed: number;
  /** Water half-width, metres (grows downstream). */
  half: number;
  /** Accumulated downstream arclength from the source, metres. Monotonic along
   *  the polyline — the ribbon V coordinate and the continuous flow phase
   *  (dot-against-local-tangent sawtoothed every densify step; this doesn't). */
  s: number;
  /** Local current speed, m/s — from the water-surface gradient over a short
   *  window (steep reaches run fast, lowland reaches drift). Drives both the
   *  gameplay current and the rendered flow rate. */
  speed: number;
  /** Downstream flow tangent (unit, tangent to the sphere). */
  fx: number;
  fy: number;
  fz: number;
}

export interface RiverPolyline {
  id: number;
  points: RiverPoint[];
  /** Explicit native canyon ownership, only after final wet-route validation. */
  canyonId?: string;
}

/** What the terrain carve needs at one direction (or null if no river near). */
export interface RiverHit {
  riverId: number;
  /** Local surface grade in amplitude units per metre downstream. */
  slope: number;
  fall?: RiverFall;
  /** Specialist canyon geometry owns its own bed and bank structure. */
  canyonOwned?: boolean;
  /** New river geology is limited to native living terrestrial/ocean worlds. */
  landformsEnabled?:boolean;
  pool?:RiverPoolDef;
  poolInfluence?:number;
  /** Cosmetic/bed width only. waterHalf remains the original current width. */
  effectiveWaterHalf?:number;
  /** Potential local-water ownership; never exceeds the original carveHalf. */
  claimHalf?:number;
  cross?: number;
  /** Smoothed route curvature response, -1..1. Positive turns toward the
   * cross-positive bank (downstream tangent × radial up); that bank is inside
   * the bend. Cached privately by RiverGraph, never serialized on RiverPoint. */
  bankTurn?: number;
  /** Normal to the continuous local water cross-section (sphere tangent).
   * Rendering uses this same chart for rows, especially at waterfall lips. */
  sectionx?:number;
  sectiony?:number;
  sectionz?:number;
  /** Continuous surface shared by physically joining ordinary rivers. */
  junctionId?:number;
  /** Metres to the nearest centreline point. */
  dist: number;
  /** Water-surface elevation, amplitude units. */
  ws: number;
  /** Channel-bed elevation, amplitude units. */
  bed: number;
  /** Water half-width, metres. */
  waterHalf: number;
  /** Carve (bank) half-width, metres. */
  carveHalf: number;
  /** Continuous downstream coordinate of the owning route, metres. */
  s: number;
  /** Local current speed, metres per second. */
  speed: number;
  fx: number;
  fy: number;
  fz: number;
}

/** Shared placement contract used by terrain, water meshes and gameplay. */
export interface RiverSource {
  carveAt(dx: number, dy: number, dz: number): RiverHit | null;
  readonly rivers: readonly RiverPolyline[];
}

const TRACE_STEP_M = 20;
const MAX_TRACE_STEPS = 1300; // bounded to about 26 km
const STALE_LIMIT = 8;
const DENSIFY_M = 10;
const MIN_POINTS = 14;
const WATER_HALF_MIN = 5;
const WATER_HALF_MAX = 18;
/** Maximum bank influence; the renderer resolves the connected wet shoreline inside it. */
export const RIVER_BANK_FACTOR = 2.6;
/** Prescribed centre-channel water depth, metres. */
export const RIVER_CHANNEL_DEPTH_M = 3.5;
/** Broad proximity-query band only. No terrain is changed beyond the banks. */
export const RIVER_VALLEY_FACTOR = 2.2;
/** Moisture on low, connected banks, using the proximity hit already needed
 * for carving. Metre-based falloffs end inside the query footprint; high
 * cliffs and land well below an adjacent perched reach receive no bonus. */
export function riverBankMoisture(h: number, hit: RiverHit, amplitude: number): number {
  const smooth = (lo: number, hi: number, x: number): number => {
    const k = Math.min(1, Math.max(0, (x - lo) / (hi - lo)));
    return k * k * (3 - 2 * k);
  };
  const above = (h - hit.ws) * amplitude;
  const edge = Math.min(hit.carveHalf * RIVER_VALLEY_FACTOR, hit.waterHalf + 28);
  return (1 - smooth(hit.waterHalf, edge, hit.dist))
    * (1 - smooth(1.2, 7, above)) * smooth(-6, -1, above);
}
/** Piecewise-linear strata exposure: finite, continuous and seed-stable. The
 * corners describe bank headlands instead of smooth periodic scallops. */
function bankExposure(s: number, river: number, side: number): number {
  const at = s/14+side*7.3;
  const cell = Math.floor(at), f = at-cell;
  const hash = (i: number): number => {
    let n = Math.imul(i,374761393)^Math.imul(river+17,668265263);
    n = Math.imul(n^(n>>>13),1274126177);
    return ((n^(n>>>16))>>>0)/4294967295;
  };
  return hash(cell)*(1-f)+hash(cell+1)*f;
}
/** Shared carve profile, also used to resolve a waterfall's wet lip width. */
export function carveRiverHeight(h: number, hit: RiverHit, amplitude: number): number {
  const t = Math.min(1, hit.dist / hit.carveHalf);
  const bank = 1 - t*t*(3-2*t);
  // Never flatten a valley or raise levees to rescue an invalid route. The
  // finite incision cap applies to the whole footprint, including fall pools.
  const limit = hit.fall ? RIVER_MAX_CUT_M : RIVER_CHANNEL_DEPTH_M + 3.5;
  const cut = Math.min(limit / amplitude, Math.max(0, h-hit.bed));
  const round = h-cut*bank*bank;
  if (t >= 1) return h;
  // Erosion exposes a low bench between the channel face and the outer bank.
  // Long, continuous variations alternate the broad shelf and cut-bank sides;
  // they are functions of river arclength, never of mesh LOD or tile coordinates.
  const smooth = (a: number, b: number, x: number): number => {
    const q = Math.max(0, Math.min(1, (x-a)/(b-a)));
    return q*q*(3-2*q);
  };
  const phase = hit.riverId*2.3999632297;
  const bend = pmath.sin(hit.s/83+phase);
  const side = (hit.cross ?? 0) < 0 ? -1 : 1;
  const exposure = .5+.5*side*bend;
  const strata = bankExposure(hit.s,hit.riverId,side);
  const width = hit.waterHalf*(1.12+.13*side*bend+.72*(strata-.5));
  const x = hit.dist/width;
  // The wet face spans 2–4 m on ordinary reaches. Wide shelves alternate with
  // narrow ledges, instead of making two identical terraces down every river.
  const shelf = hit.ws+(.28+1.1*strata+.18*exposure)/amplitude;
  // Round the lip over multiple terrain samples. A hard derivative break at
  // the shelf made interpolated cliff/snow coverage form regular sawteeth.
  const inner = smooth(.48,1.02,x);
  const bedToShelf = hit.bed+(shelf-hit.bed)*inner;
  const outerStart = 1.08+.32*exposure;
  const outerEnd = Math.min(hit.carveHalf/width, outerStart+.42);
  const target = bedToShelf+(h-bedToShelf)*smooth(outerStart,outerEnd,x);
  const shaped = h-Math.min(limit/amplitude, Math.max(0,h-target));
  // Keep the existing waterfall cliff/lip contract, feathering back into it
  // over its full approach and receiving pool. Sources open gradually too.
  const strength = smooth(0,35,hit.s)*smooth(.18,.58,strata)
    *(hit.fall ? 1-fallInfluence(hit.fall,hit.s) : 1);
  const carved=carveRiverLedgeHeight(h,carveRiverPoolHeight(h,round+(shaped-round)*strength,hit,amplitude),hit,amplitude);
  return carveWaterfallBankHeight(h,carved,hit,amplitude);
}
const BANK_FACTOR = RIVER_BANK_FACTOR;
const CHANNEL_DEPTH_M = RIVER_CHANNEL_DEPTH_M;
const VALLEY_FACTOR = RIVER_VALLEY_FACTOR;
const GRID_G = 96; // spatial-hash cells per cube-face axis
const MAX_SOURCE_ATTEMPTS = 256;
const MAX_BREACH_M = 3; // refuse routes that would need a deep cut through a rise

// --- Small vector helpers (build-time only; clarity over allocation) -------
interface V3 {
  x: number;
  y: number;
  z: number;
}
const norm = (v: V3): V3 => {
  const l = pmath.hypot(v.x, v.y, v.z) || 1;
  return { x: v.x / l, y: v.y / l, z: v.z / l };
};

/** Tangent basis (t1, t2) at a unit direction — matches surfaceNormal's basis. */
function tangentBasis(d: V3): [V3, V3] {
  const ax = Math.abs(d.x) < 0.9 ? 1 : 0;
  const ay = 1 - ax;
  const t1 = norm({ x: ay * d.z, y: -ax * d.z, z: ax * d.y - ay * d.x });
  const t2 = { x: d.y * t1.z - d.z * t1.y, y: d.z * t1.x - d.x * t1.z, z: d.x * t1.y - d.y * t1.x };
  return [t1, t2];
}

/**
 * Cube-face cell index of a unit direction — a stable bucket id. The exact face
 * orientation is irrelevant; only "same dir → same cell" and "nearby dirs →
 * nearby cells" matter (and seams are handled by smearing at insert time).
 */
function cellOf(x: number, y: number, z: number): number {
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  const az = Math.abs(z);
  let face: number;
  let u: number;
  let v: number;
  if (ax >= ay && ax >= az) {
    const s = 1 / ax;
    face = x > 0 ? 0 : 1;
    u = (x > 0 ? -z : z) * s;
    v = y * s;
  } else if (ay >= az) {
    const s = 1 / ay;
    face = y > 0 ? 2 : 3;
    u = x * s;
    v = (y > 0 ? -z : z) * s;
  } else {
    const s = 1 / az;
    face = z > 0 ? 4 : 5;
    u = (z > 0 ? x : -x) * s;
    v = y * s;
  }
  const cu = Math.min(GRID_G - 1, Math.max(0, ((u * 0.5 + 0.5) * GRID_G) | 0));
  const cv = Math.min(GRID_G - 1, Math.max(0, ((v * 0.5 + 0.5) * GRID_G) | 0));
  return (face * GRID_G + cv) * GRID_G + cu;
}

/** Follow the full terrain downhill, allowing only a small bounded breach.
 * Closed basins that cannot drain within that budget are not forced through mountains. */
function traceRiver(sampler: PlanetSampler, start: V3, seaRel: number): V3[] | null {
  const R = sampler.def.radius, A = sampler.amplitude;
  const stepA = TRACE_STEP_M / R;
  const height = (p: V3) => sampler.drainageH(p.x, p.y, p.z) * A;
  let c = norm(start), low = height(c), stale = 0;
  let prev: V3 | null = null;
  const path: V3[] = [];
  for (let s = 0; s < MAX_TRACE_STEPS; s++) {
    const h = height(c);
    if (h - low > MAX_BREACH_M) return null;
    low = Math.min(low, h);
    path.push(c);
    if (h < seaRel * A - .7) return path.length >= MIN_POINTS ? path : null;
    const [u, v] = tangentBasis(c);
    let best: V3 | null = null, bestDir: V3 | null = null, bestH = Infinity, score = Infinity;
    for (let k = 0; k < 16; k++) {
      const a = k * Math.PI / 8, ca = pmath.cos(a), sa = pmath.sin(a);
      const dir = {x:u.x*ca+v.x*sa,y:u.y*ca+v.y*sa,z:u.z*ca+v.z*sa};
      const turn = prev ? prev.x*dir.x+prev.y*dir.y+prev.z*dir.z : 1;
      if (turn < -.25) continue;
      const p = norm({x:c.x+dir.x*stepA,y:c.y+dir.y*stepA,z:c.z+dir.z*stepA});
      const nh = height(p), cost = nh + (1-turn)*.12;
      if (cost < score) {best=p;bestDir=dir;bestH=nh;score=cost;}
    }
    if (!best || !bestDir) return null;
    if (bestH > low + .02) {if (++stale > STALE_LIMIT) return null;} else stale=0;
    c=best;prev=bestDir;
  }
  return null;
}

/** Densify a coarse traced path and assign descending bed, widths and flow. */
function buildPolyline(sampler: PlanetSampler, path: V3[], id: number): RiverPolyline {
  const R = sampler.def.radius;
  const depthAmp = CHANNEL_DEPTH_M / sampler.amplitude;

  // Densify by slerp-ish normalized lerp so nearest-point ≈ distance-to-centreline.
  const dense: V3[] = [];
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const b = path[i + 1];
    const arc = pmath.acos(Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z)));
    const sub = Math.max(1, Math.round((arc * R) / DENSIFY_M));
    for (let k = 0; k < sub; k++) {
      const t = k / sub;
      dense.push(norm({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t }));
    }
  }
  dense.push(path[path.length - 1]);

  const n = dense.length;
  const points: RiverPoint[] = new Array(n);
  // A shallow channel follows the actual terrain's running low, with a
  // bounded breach checked by the tracer. The continental datum plays no role.
  const natLift = -1.8 / sampler.amplitude;
  const wsFloor = sampler.seaRel - .5 / sampler.amplitude;
  let ws = sampler.drainageH(dense[0].x, dense[0].y, dense[0].z) + natLift;
  let sAcc = 0; // accumulated downstream arclength, metres
  for (let i = 0; i < n; i++) {
    const d = dense[i];
    if (i > 0) {
      const p = dense[i - 1];
      const dot = Math.max(-1, Math.min(1, d.x * p.x + d.y * p.y + d.z * p.z));
      sAcc += pmath.acos(dot) * R;
    }
    const natural = sampler.drainageH(d.x, d.y, d.z);
    ws = Math.max(wsFloor, Math.min(ws, natural + natLift));
    // Grow with catchment length, not a short route's percentage: the old
    // 8→45 m ramp turned even a 300 m stream into a broad triangular slab.
    const half = Math.min(WATER_HALF_MAX, WATER_HALF_MIN + .19 * Math.sqrt(sAcc));
    // Flow tangent = direction to the next point (last reuses the previous).
    const nx = dense[Math.min(i + 1, n - 1)];
    let fx = nx.x - d.x;
    let fy = nx.y - d.y;
    let fz = nx.z - d.z;
    const rad = fx * d.x + fy * d.y + fz * d.z;
    fx -= rad * d.x;
    fy -= rad * d.y;
    fz -= rad * d.z;
    const fl = pmath.hypot(fx, fy, fz) || 1;
    points[i] = { x: d.x, y: d.y, z: d.z, ws, bed: ws - depthAmp, half, s: sAcc, speed: 0, fx: fx / fl, fy: fy / fl, fz: fz / fl };
  }
  // Fix the very last flow (it copied itself → use the previous segment's).
  if (n >= 2) {
    points[n - 1].fx = points[n - 2].fx;
    points[n - 1].fy = points[n - 2].fy;
    points[n - 1].fz = points[n - 2].fz;
  }
  // ESTUARY FUNNEL: widen the channel over the last ~120 m so the river
  // SPREADS into the sea — a fan of water opening onto the submerged mouth
  // basin (ws floor above) instead of a constant-width strip that just stops.
  // Quadratic ease keeps the upstream course untouched.
  const sTotal = points[n - 1].s;
  for (let i = 0; i < n; i++) {
    const k = 1 - Math.min(1, (sTotal - points[i].s) / 120);
    points[i].half *= 1 + .6 * k * k;
  }
  // Current speed from the water-surface gradient over a ±40 m window: steep
  // reaches (mountain streams) run near the cap, lowland reaches drift. Pure
  // function of ws/s so client + server agree byte-for-byte.
  for (let i = 0; i < n; i++) {
    const a = points[Math.max(0, i - 4)];
    const b = points[Math.min(n - 1, i + 4)];
    const ds = b.s - a.s;
    const slope = ds > 1 ? ((a.ws - b.ws) * sampler.amplitude) / ds : 0; // m drop per m run
    points[i].speed = Math.min(3.4, Math.max(0.9, 0.9 + 11 * slope));
    // A steep headwater is a contained chute, not a lowland-width sheet spread
    // across an open hillside. Broad reaches develop where the grade relaxes.
    points[i].half *= .3 + .7 / (1 + 5 * Math.max(0,slope));
  }
  return { id, points };
}

/** The default RiverSource: trace-built polylines + a seam-safe spatial hash. */
export class RiverGraph implements RiverSource {
  readonly version = HYDROLOGY_VERSION;
  readonly rivers: readonly RiverPolyline[];
  readonly falls: readonly RiverFall[];
  readonly pools:readonly RiverPoolDef[];
  readonly escarpments:readonly RiverEscarpment[];
  private readonly fallsByRiver = new Map<number, RiverFall[]>();
  private readonly poolsByFall = new Map<string,RiverPoolDef>();
  private readonly R: number;
  private readonly R2: number;
  // Flat point arrays for the hot query path.
  private readonly px: Float64Array;
  private readonly py: Float64Array;
  private readonly pz: Float64Array;
  private readonly ws: Float64Array;
  private readonly bed: Float64Array;
  private readonly half: Float64Array;
  private readonly sArc: Float64Array;
  private readonly spd: Float64Array;
  private readonly fx: Float64Array;
  private readonly fy: Float64Array;
  private readonly fz: Float64Array;
  private readonly bankTurn: Float64Array;
  /** Shared cross-section planes at route joints. Adjacent segment charts
   * meet at the SAME plane, so nearest-segment bisectors cannot step water. */
  private readonly sectionX: Float64Array;
  private readonly sectionY: Float64Array;
  private readonly sectionZ: Float64Array;
  private readonly sectionStartScale: Float64Array;
  private readonly sectionEndScale: Float64Array;
  /** River index of each flat point — segment interpolation must never lerp
   *  across the boundary between two different rivers' point runs. */
  private readonly rid: Int32Array;
  private readonly grid = new Map<number, number[]>();
  private readonly junctionPeers = new Map<number,Set<number>>();
  private readonly junctionCells = new Set<number>();
  get hasConfluences():boolean{return this.junctionPeers.size>0;}

  constructor(rivers: RiverPolyline[], radius: number, falls: RiverFall[] = [],poolTerrain?:RiverPoolTerrain,private readonly landformsEnabled=true,escarpments:readonly RiverEscarpment[]=[]) {
    this.escarpments=escarpments;
    this.rivers = rivers;
    this.falls = falls;
    for (const fall of falls) {
      const list = this.fallsByRiver.get(fall.riverId) ?? [];
      list.push(fall);
      this.fallsByRiver.set(fall.riverId, list);
    }
    this.R = radius;
    this.R2 = radius * radius;
    const all: RiverPoint[] = [];
    const allRid: number[] = [];
    for (let r = 0; r < rivers.length; r++)
      for (const p of rivers[r].points) {
        all.push(p);
        allRid.push(r);
      }
    const n = all.length;
    this.rid = Int32Array.from(allRid);
    this.px = new Float64Array(n);
    this.py = new Float64Array(n);
    this.pz = new Float64Array(n);
    this.ws = new Float64Array(n);
    this.bed = new Float64Array(n);
    this.half = new Float64Array(n);
    this.sArc = new Float64Array(n);
    this.spd = new Float64Array(n);
    this.fx = new Float64Array(n);
    this.fy = new Float64Array(n);
    this.fz = new Float64Array(n);
    this.bankTurn = new Float64Array(n);
    this.sectionX = new Float64Array(n);
    this.sectionY = new Float64Array(n);
    this.sectionZ = new Float64Array(n);
    this.sectionStartScale = new Float64Array(n);
    this.sectionEndScale = new Float64Array(n);
    for(let i=0;i<n;i++){
      const p=all[i],a=i>0&&allRid[i-1]===allRid[i]?all[i-1]:p;
      const b=i+1<n&&allRid[i+1]===allRid[i]?all[i+1]:p;
      let ax=p.x-a.x,ay=p.y-a.y,az=p.z-a.z,bx=b.x-p.x,by=b.y-p.y,bz=b.z-p.z;
      const al=pmath.hypot(ax,ay,az)||1,bl=pmath.hypot(bx,by,bz)||1;
      let x=ax/al+bx/bl,y=ay/al+by/bl,z=az/al+bz/bl;
      const radial=x*p.x+y*p.y+z*p.z;x-=radial*p.x;y-=radial*p.y;z-=radial*p.z;
      const length=pmath.hypot(x,y,z)||1;
      this.sectionX[i]=x/length;this.sectionY[i]=y/length;this.sectionZ[i]=z/length;
    }
    for(let i=0;i<n-1;i++)if(allRid[i]===allRid[i+1]){
      const a=all[i],b=all[i+1],x=b.x-a.x,y=b.y-a.y,z=b.z-a.z;
      const start=x*this.sectionX[i]+y*this.sectionY[i]+z*this.sectionZ[i];
      const end=x*this.sectionX[i+1]+y*this.sectionY[i+1]+z*this.sectionZ[i+1];
      // A reversing or degenerate route has no regular cross-section chart.
      if(start>1e-12&&end>1e-12){this.sectionStartScale[i]=1/start;this.sectionEndScale[i]=1/end;}
    }
    // Actual polyline curvature over an 80 m neighborhood. The finite window
    // ignores single densification corners; arclength makes the response
    // independent of route point spacing. This does not alter any route data.
    let offset=0;
    for(const route of rivers){
      const points=route.points;let lo=0,hi=0;
      for(let i=0;i<points.length;i++){
        const p=points[i];
        while(lo+1<i&&p.s-points[lo+1].s>=40)lo++;
        while(hi<points.length-1&&points[hi].s-p.s<40)hi++;
        if(lo===i||hi===i)continue;
        const a=points[lo],b=points[hi],run=(b.s-a.s)*.5;
        let ax=p.x-a.x,ay=p.y-a.y,az=p.z-a.z,bx=b.x-p.x,by=b.y-p.y,bz=b.z-p.z;
        const ad=ax*p.x+ay*p.y+az*p.z,bd=bx*p.x+by*p.y+bz*p.z;
        ax-=ad*p.x;ay-=ad*p.y;az-=ad*p.z;bx-=bd*p.x;by-=bd*p.y;bz-=bd*p.z;
        const al=pmath.hypot(ax,ay,az),bl=pmath.hypot(bx,by,bz);
        if(al<1e-12||bl<1e-12||run<1)continue;
        const turn=-((ay*bz-az*by)*p.x+(az*bx-ax*bz)*p.y+(ax*by-ay*bx)*p.z)/(al*bl*run);
        this.bankTurn[offset+i]=Math.max(-1,Math.min(1,turn*72));
      }
      offset+=points.length;
    }
    for (let i = 0; i < n; i++) {
      const p = all[i];
      this.px[i] = p.x;
      this.py[i] = p.y;
      this.pz[i] = p.z;
      this.ws[i] = p.ws;
      this.bed[i] = p.bed;
      this.half[i] = p.half;
      this.sArc[i] = p.s;
      this.spd[i] = p.speed;
      this.fx[i] = p.fx;
      this.fy[i] = p.fy;
      this.fz[i] = p.fz;
      this.smear(i, p);
    }
    // Only actual tail-to-host joins may blend. Nearby perched channels keep
    // their own water, and explicit waterfall/canyon reaches never donate a
    // surface. This is derived from unchanged route data, without new RNG.
    for(let r=0;r<rivers.length;r++)for(let host=0;host<rivers.length;host++){
      if(r===host||rivers[r].canyonId||rivers[host].canyonId)continue;
      const tail=rivers[r].points,other=rivers[host].points;
      let joined=false;
      for(let k=Math.max(0,tail.length-4);k<tail.length&&!joined;k++)for(let i=1;i<other.length;i++){
        const p=tail[k],a=other[i-1],b=other[i],x=p.x-a.x,y=p.y-a.y,z=p.z-a.z;
        const vx=b.x-a.x,vy=b.y-a.y,vz=b.z-a.z,vv=vx*vx+vy*vy+vz*vz;
        if(vv<1e-24)continue;
        const t=Math.max(0,Math.min(1,(x*vx+y*vy+z*vz)/vv));
        const dx=x-t*vx,dy=y-t*vy,dz=z-t*vz,half=a.half+(b.half-a.half)*t;
        if((dx*dx+dy*dy+dz*dz)*this.R2>half*half)continue;
        const ws=a.ws+(b.ws-a.ws)*t,depth=Math.min(p.ws-p.bed,a.ws-a.bed,b.ws-b.bed);
        if(Math.abs(p.ws-ws)>depth*.5)continue;
        const arc=a.s+(b.s-a.s)*t;
        if(falls.some(f=>(f.riverId===rivers[r].id&&fallInfluence(f,p.s)>0)||(f.riverId===rivers[host].id&&fallInfluence(f,arc)>0)))continue;
        // The tail must enter from outside the host. Two unrelated parallel
        // streams do not become a confluence merely because their ends are near.
        const approach=tail[Math.max(0,k-6)];let priorD2=Infinity;
        for(let n=1;n<other.length;n++){
          const aa=other[n-1],bb=other[n],vx=bb.x-aa.x,vy=bb.y-aa.y,vz=bb.z-aa.z;
          const x=approach.x-aa.x,y=approach.y-aa.y,z=approach.z-aa.z,vv=vx*vx+vy*vy+vz*vz;
          const q=vv>1e-24?Math.max(0,Math.min(1,(x*vx+y*vy+z*vz)/vv)):0;
          const px=x-q*vx,py=y-q*vy,pz=z-q*vz;
          priorD2=Math.min(priorD2,(px*px+py*py+pz*pz)*this.R2);
        }
        if(priorD2<=half*half*1.44)continue;
        joined=true;break;
      }
      if(joined){for(const [a,b]of[[r,host],[host,r]]){let peers=this.junctionPeers.get(a);if(!peers)this.junctionPeers.set(a,peers=new Set());peers.add(b);}}
    }
    // All branches of one verified network use the same contributors at a
    // multiple junction; otherwise exchanging two tributaries could change
    // the set of blend weights even though each joins the same host.
    for(const owner of this.junctionPeers.keys()){
      const connected=new Set<number>(),pending=[owner];
      while(pending.length){const next=pending.pop()!;if(connected.has(next))continue;connected.add(next);for(const peer of this.junctionPeers.get(next)??[])pending.push(peer);}
      connected.delete(owner);this.junctionPeers.set(owner,connected);
    }
    if(this.junctionPeers.size)for(const [cell,bucket]of this.grid){
      const owners=new Set(bucket.map(i=>this.rid[i]));
      if([...owners].some(r=>[...this.junctionPeers.get(r)??[]].some(p=>owners.has(p))))this.junctionCells.add(cell);
    }
    this.pools=poolTerrain&&landformsEnabled?buildRiverPools(rivers,falls,radius,poolTerrain,(x,y,z)=>this.carveAt(x,y,z)):[];
    for(const pool of this.pools)this.poolsByFall.set(pool.fallId,pool);
  }

  /** Insert point i into every cell within its carve radius (seam-safe via 3D offsets). */
  private smear(i: number, p: RiverPoint): void {
    const smearR = p.half * BANK_FACTOR + (this.R * Math.PI * 0.5) / GRID_G; // bank + one cell
    const d: V3 = { x: p.x, y: p.y, z: p.z };
    const [t1, t2] = tangentBasis(d);
    const seen = new Set<number>();
    const add = (dir: V3): void => {
      const cell = cellOf(dir.x, dir.y, dir.z);
      if (seen.has(cell)) return;
      seen.add(cell);
      let bucket = this.grid.get(cell);
      if (!bucket) this.grid.set(cell, (bucket = []));
      bucket.push(i);
    };
    add(d);
    for (let ring = 0; ring < 2; ring++) {
      const rm = (smearR * (ring === 0 ? 0.55 : 1.0)) / this.R;
      for (let a = 0; a < 8; a++) {
        const ang = (a / 8) * Math.PI * 2;
        const ca = pmath.cos(ang) * rm;
        const sa = pmath.sin(ang) * rm;
        add(norm({ x: d.x + t1.x * ca + t2.x * sa, y: d.y + t1.y * ca + t2.y * sa, z: d.z + t1.z * ca + t2.z * sa }));
      }
    }
  }

  carveAt(dx: number, dy: number, dz: number): RiverHit | null {
    const cell=cellOf(dx,dy,dz),bucket = this.grid.get(cell);
    if (bucket === undefined) return null;
    // Ordinary cells retain one nearest-owner search. Only verified joining
    // cells need per-owner candidates; at most the finite route count applies.
    const candidates=this.junctionCells.has(cell)?new Map<number,{i:number;j:number;t:number;d2:number}>():null;
    let best = -1, j = -1, t = 0, bestD2 = Infinity;
    // Compare actual nearby segments, including other river owners. Selecting
    // one nearest sampled point first lets a tributary steal the host channel
    // between its bracketing points and creates dry gaps at a valid confluence.
    for (let b = 0; b < bucket.length; b++) {
      const i=bucket[b],ax=dx-this.px[i],ay=dy-this.py[i],az=dz-this.pz[i];
      const pointD2=(ax*ax+ay*ay+az*az)*this.R2;
      if(pointD2<bestD2){bestD2=pointD2;best=i;j=i;t=0;}
      if(candidates&&pointD2<(candidates.get(this.rid[i])?.d2??Infinity))candidates.set(this.rid[i],{i,j:i,t:0,d2:pointD2});
      for(let sign=-1;sign<=1;sign+=2){
        const n=i+sign;if(n<0||n>=this.px.length||this.rid[n]!==this.rid[i])continue;
        const bx=this.px[n]-this.px[i],by=this.py[n]-this.py[i],bz=this.pz[n]-this.pz[i],ab2=bx*bx+by*by+bz*bz;
        if(ab2<1e-24)continue;
        const tt=Math.max(0,Math.min(1,(ax*bx+ay*by+az*bz)/ab2)),px=ax-bx*tt,py=ay-by*tt,pz=az-bz*tt,d2=(px*px+py*py+pz*pz)*this.R2;
        if(d2<bestD2){bestD2=d2;best=i;j=n;t=tt;}
        if(candidates&&d2<(candidates.get(this.rid[i])?.d2??Infinity))candidates.set(this.rid[i],{i,j:n,t:tt,d2});
      }
    }
    if(best<0)return null;
    const hit=this.hitAt(dx,dy,dz,best,j,t,bestD2);
    if(!hit||!candidates||hit.fall||hit.canyonOwned||hit.dist>=hit.carveHalf)return hit;
    const peers=this.junctionPeers.get(this.rid[best]);if(!peers)return hit;
    // Compact regularized inverse-distance weights are identical from either
    // owner and fade at the existing bank. The finite core gives even an
    // imperfectly matched crossing ONE limiting height and current. Outside
    // the overlap, authored levels are exact; source descriptors never change.
    const weight=(h:RiverHit)=>{const t=h.dist/h.carveHalf,k=Math.max(0,1-t*t),core=h.waterHalf*.2;return k*k/(h.dist*h.dist+core*core);};
    let sum=weight(hit),ws=hit.ws*sum,bed=hit.bed*sum,speed=hit.speed*sum;
    let fx=hit.fx*sum,fy=hit.fy*sum,fz=hit.fz*sum,slope=hit.slope*sum;
    let blended=false;
    for(const [owner,c]of candidates){
      if(!peers.has(owner))continue;
      const other=this.hitAt(dx,dy,dz,c.i,c.j,c.t,c.d2);
      if(!other||other.fall||other.canyonOwned||other.dist>=other.carveHalf)continue;
      const w=weight(other);sum+=w;ws+=other.ws*w;bed+=other.bed*w;speed+=other.speed*w;
      fx+=other.fx*w;fy+=other.fy*w;fz+=other.fz*w;slope+=other.slope*w;blended=true;
    }
    if(blended&&sum>0){
      hit.ws=ws/sum;hit.bed=bed/sum;hit.speed=speed/sum;hit.slope=slope/sum;
      hit.junctionId=Math.min(hit.riverId,...[...peers].map(r=>this.rivers[r].id));
      const fl=pmath.hypot(fx,fy,fz)||1;hit.fx=fx/fl;hit.fy=fy/fl;hit.fz=fz/fl;
    }
    return hit;
  }

  private hitAt(dx:number,dy:number,dz:number,best:number,j:number,t:number,bestD2:number):RiverHit|null {
    const nearestHalf=this.half[best]+(this.half[j]-this.half[best])*t;
    const maxDistance=nearestHalf*BANK_FACTOR*VALLEY_FACTOR;
    if(bestD2>maxDistance*maxDistance)return null;
    // Use the closest route for ownership/distance, then locate its adjoining
    // miter strip. A perpendicular nearest-segment projection has TWO values
    // at an inside bend; these endpoint planes instead agree at the joint.
    // Scaled signed distances preserve linear centreline arclength exactly:
    // on A+t(B-A), start=t and end=t-1, hence u=start/(start-end)=t.
    let segment=Math.min(best,j);
    if(segment+1>=this.px.length||this.rid[segment+1]!==this.rid[best])segment--;
    for(let step=0;step<8;step++){
      if(segment<0||segment+1>=this.px.length||this.rid[segment]!==this.rid[best]||this.rid[segment+1]!==this.rid[best]||this.sectionStartScale[segment]===0)break;
      const next=segment+1;
      const a=((dx-this.px[segment])*this.sectionX[segment]+(dy-this.py[segment])*this.sectionY[segment]+(dz-this.pz[segment])*this.sectionZ[segment])*this.sectionStartScale[segment];
      const b=((dx-this.px[next])*this.sectionX[next]+(dy-this.py[next])*this.sectionY[next]+(dz-this.pz[next])*this.sectionZ[next])*this.sectionEndScale[segment];
      if(a>=-1e-9&&b<=1e-9&&a-b>1e-9){best=segment;j=next;t=Math.max(0,Math.min(1,a/(a-b)));break;}
      segment+=a<0?-1:1;
    }
    // A cell straddling three cube faces may contain the joint but omit the
    // onward point. The chart walk can recover that adjoining segment; its
    // geometric distance must join the minimum as well as its water height.
    if(best!==j){
      const vx=this.px[j]-this.px[best],vy=this.py[j]-this.py[best],vz=this.pz[j]-this.pz[best];
      const ax=dx-this.px[best],ay=dy-this.py[best],az=dz-this.pz[best],vv=vx*vx+vy*vy+vz*vz;
      const q=vv>1e-24?Math.max(0,Math.min(1,(ax*vx+ay*vy+az*vz)/vv)):0;
      const x=ax-q*vx,y=ay-q*vy,z=az-q*vz;
      bestD2=Math.min(bestD2,(x*x+y*y+z*z)*this.R2);
    }
    const lerp = (arr: Float64Array): number => arr[best] + (arr[j] - arr[best]) * t;
    const s = lerp(this.sArc);
    const candidates=this.fallsByRiver.get(this.rivers[this.rid[best]].id);
    // Close cascade stages have overlapping atmosphere/bank influence. Water
    // belongs to the containing fall first; a previous stage cannot steal it.
    let selected:RiverFall|undefined,distance=Infinity;
    for(const candidate of candidates??[]){
      if(s>=candidate.lip.s&&s<=candidate.end.s){selected=candidate;break;}
      if(fallInfluence(candidate,s)<=0)continue;
      const d=Math.min(Math.abs(s-candidate.lip.s),Math.abs(s-candidate.end.s));
      if(d<distance){selected=candidate;distance=d;}
    }
    const fall=selected;
    const falling = fall && s > fall.lip.s + 1e-5 && s < fall.end.s;
    const originalHalf = lerp(this.half);
    const waterHalf = originalHalf;
    const carveHalf = originalHalf * BANK_FACTOR;
    const dist = Math.sqrt(bestD2);
    // Keep broad proximity queries for placement; carving itself stops at the bank.
    if (dist > carveHalf * VALLEY_FACTOR) return null;
    let hfx = lerp(this.fx);
    let hfy = lerp(this.fy);
    let hfz = lerp(this.fz);
    const fl = pmath.hypot(hfx, hfy, hfz) || 1;
    hfx /= fl;
    hfy /= fl;
    hfz /= fl;
    const cross = ((dx-lerp(this.px))*(hfy*dz-hfz*dy) + (dy-lerp(this.py))*(hfz*dx-hfx*dz) + (dz-lerp(this.pz))*(hfx*dy-hfy*dx))*this.R;
    const gradeEnd = j !== best ? j : best+1<this.px.length && this.rid[best+1]===this.rid[best] ? best+1 : best-1;
    const gradeRun = gradeEnd>=0 && this.rid[gradeEnd]===this.rid[best] ? this.sArc[gradeEnd]-this.sArc[best] : 0;
    const pool=fall?this.poolsByFall.get(fall.id):undefined,poolInfluence=pool?riverPoolInfluence(pool,s):0;
    const chart=best+1===j&&this.sectionStartScale[best]>0;
    const ns=chart?(1-t)*this.sectionStartScale[best]:1,ne=chart?t*this.sectionEndScale[best]:0;
    let sectionx=this.sectionX[best]*ns+this.sectionX[j]*ne;
    let sectiony=this.sectionY[best]*ns+this.sectionY[j]*ne;
    let sectionz=this.sectionZ[best]*ns+this.sectionZ[j]*ne;
    const nl=pmath.hypot(sectionx,sectiony,sectionz);
    if(nl>1e-12){sectionx/=nl;sectiony/=nl;sectionz/=nl;}
    else{sectionx=hfx;sectiony=hfy;sectionz=hfz;}
    return {
      riverId: this.rivers[this.rid[best]].id,
      canyonOwned:!!this.rivers[this.rid[best]].canyonId,
      landformsEnabled:this.landformsEnabled,
      pool:poolInfluence>0?pool:undefined,
      poolInfluence,
      effectiveWaterHalf:pool?riverPoolWaterHalf(pool,s,originalHalf):originalHalf,
      claimHalf:carveHalf,
      slope: !falling && Math.abs(gradeRun)>.01 ? Math.max(0,(this.ws[best]-this.ws[gradeEnd])/gradeRun) : 0,
      fall,
      cross,
      bankTurn: lerp(this.bankTurn),
      sectionx,sectiony,sectionz,
      dist,
      ws: falling ? fall.end.ws : lerp(this.ws),
      bed: falling ? fallBed(fall, s) : lerp(this.bed),
      waterHalf,
      carveHalf,
      s,
      speed: lerp(this.spd),
      fx: hfx,
      fy: hfy,
      fz: hfz,
    };
  }

  /**
   * Nearest river centreline distance (metres) to a unit direction, or null if
   * no centreline point lies within `maxM`. Unlike carveAt (which is bounded by
   * the carve half-width and so can never see past ~carveHalf ≈ 117 m), this is
   * a real bounded distance query over the spatial hash: it probes the centre
   * cell plus concentric rings out to `maxM` — mirroring smear()'s insert
   * pattern — dedupes cells by id, and reports the minimum chord distance.
   * Deterministic; the probe count is bounded by (maxM / cellWidth) rings, each
   * with ≤ 24 angular samples. Coverage at the disc edge is APPROXIMATE (a point
   * just past a probed cell's far corner could be missed) — acceptable for an
   * adjacency-band query, and deterministic.
   */
  nearestRiverDistM(dx: number, dy: number, dz: number, maxM: number): number | null {
    if (this.px.length === 0) return null; // empty graph
    if (maxM <= 0) return null; // no search band → nothing within it (and step=0 would loop forever)
    const cellWidthM = (this.R * Math.PI * 0.5) / GRID_G;
    const d: V3 = { x: dx, y: dy, z: dz };
    const [t1, t2] = tangentBasis(d);
    // Dedupe cells with a Set guard, but iterate an insertion-order array so the
    // scan is order-stable (deterministic) regardless of Set internals.
    const seen = new Set<number>();
    const cells: number[] = [];
    const add = (x: number, y: number, z: number): void => {
      const cell = cellOf(x, y, z); // cellOf is scale-invariant → no renormalize needed
      if (seen.has(cell)) return;
      seen.add(cell);
      cells.push(cell);
    };
    add(dx, dy, dz);
    const step = Math.min(cellWidthM, maxM) * 0.9;
    for (let ringM = step; ringM <= maxM; ringM += step) {
      const rm = ringM / this.R; // tangent offset in radians (≈ chord for small rm)
      const angles = Math.min(24, Math.max(8, Math.ceil((2 * Math.PI * ringM) / cellWidthM)));
      for (let a = 0; a < angles; a++) {
        const ang = (a / angles) * Math.PI * 2;
        const ca = pmath.cos(ang) * rm;
        const sa = pmath.sin(ang) * rm;
        add(dx + t1.x * ca + t2.x * sa, dy + t1.y * ca + t2.y * sa, dz + t1.z * ca + t2.z * sa);
      }
    }
    let bestD2 = Infinity;
    for (let c = 0; c < cells.length; c++) {
      const bucket = this.grid.get(cells[c]);
      if (bucket === undefined) continue;
      for (let b = 0; b < bucket.length; b++) {
        const i = bucket[b];
        const dot = dx * this.px[i] + dy * this.py[i] + dz * this.pz[i];
        const dd2 = Math.max(0, 2 - 2 * dot) * this.R2; // chord² in m²
        if (dd2 < bestD2) bestD2 = dd2;
      }
    }
    if (bestD2 === Infinity) return null;
    const min = Math.sqrt(bestD2);
    return min <= maxM ? min : null;
  }
}

const EMPTY_GRAPH = new RiverGraph([], 1);

/** Build (or return an empty) river graph for a sampler — deterministic from the seed. */
export function buildRiverGraph(sampler: PlanetSampler): RiverGraph {
  const def = sampler.def;
  if (def.seaRadius === null || def.terrain.riverDensity <= 0) return EMPTY_GRAPH;
  const seaRel = sampler.seaRel;
  const rng: Rng = mulberry32(hashSeed(`${def.seed}:rivers`));
  const target = Math.round(4 + def.terrain.riverDensity * 10); // ~4..14 rivers

  // Deterministic candidate sources: a Fibonacci sphere kept where there's
  // inland elevation above the sea, sorted highest-first so rivers start in the
  // highlands and run long. Robust on any land fraction (rejection sampling
  // starves on ocean worlds where land is sparse).
  const M = 1600;
  const ga = Math.PI * (3 - Math.sqrt(5));
  const seedAngle = range(rng, 0, Math.PI * 2);
  const cands: { x: number; y: number; z: number; h: number }[] = [];
  for (let i = 0; i < M; i++) {
    const yv = 1 - ((i + 0.5) / M) * 2;
    const rad = Math.sqrt(Math.max(0, 1 - yv * yv));
    const th = ga * i + seedAngle;
    const x = pmath.cos(th) * rad;
    const z = pmath.sin(th) * rad;
    const h = sampler.drainageH(x, yv, z);
    if (h > seaRel + 0.05 && sampler.glacierIceAt(x,yv,z)<=0&&!sampler.lavaFootprintAt(x,yv,z)) cands.push({ x, y: yv, z, h });
  }
  cands.sort((a, b) => b.h - a.h);

  const rivers: RiverPolyline[] = [];
  const nativeFalls:RiverFall[]=[];
  // Canyon floors already exist before hydrology. Adopt only complete native
  // coast-connected routes whose actual floor/banks contain this narrow stream;
  // ordinary rivers retain their existing excavation budget and tracing rules.
  const canyonOut=canyonLocation();
  for(const canyon of sampler.canyons()){
    if(!canyon.wet)continue;
    if(canyon.parentId&&!rivers.some(r=>r.canyonId===canyon.parentId))continue;
    const points:RiverPoint[]=[],length=canyon.points[canyon.points.length-1].s,count=Math.max(1,Math.ceil(length/10));
    let fits=true,previousWs=Infinity;
    for(let i=0;i<=count;i++){
      const p=canyonPoint(canyon,length*i/count,canyonOut),next=canyonPoint(canyon,Math.min(length,p.s+1),canyonLocation());
      let fx=next.x-p.x,fy=next.y-p.y,fz=next.z-p.z;
      const radial=fx*p.x+fy*p.y+fz*p.z;fx-=radial*p.x;fy-=radial*p.y;fz-=radial*p.z;const fl=pmath.hypot(fx,fy,fz)||1;fx/=fl;fy/=fl;fz/=fl;
      const ws=(p.floorM-.45)/sampler.amplitude,bed=(p.floorM-1.30)/sampler.amplitude,half=1.35+.45*Math.sqrt(i/count);
      const natural=sampler.drainageH(p.x,p.y,p.z),gap=(natural-ws)*sampler.amplitude;
      if(ws>previousWs+1e-12||(natural-bed)*sampler.amplitude>3||gap<-.18){fits=false;break;}
      if(ws>seaRel){const side=norm({x:fy*p.z-fz*p.y,y:fz*p.x-fx*p.z,z:fx*p.y-fy*p.x});
        for(const sign of[-1,1]){const off=sign*half*BANK_FACTOR/def.radius,d=norm({x:p.x+side.x*off,y:p.y+side.y*off,z:p.z+side.z*off});if(sampler.drainageH(d.x,d.y,d.z)<ws){fits=false;break;}}
      }
      if(!fits)break;
      const grade=i>0?Math.max(0,(previousWs-ws)*sampler.amplitude/(length/count)):0;
      points.push({x:p.x,y:p.y,z:p.z,ws,bed,half,s:p.s,speed:Math.min(2.4,.7+8*grade),fx,fy,fz});previousWs=ws;
    }
    if(!fits||points.length<14)continue;
    const last=points[points.length-1],prev=points[points.length-2];last.fx=prev.fx;last.fy=prev.fy;last.fz=prev.fz;
    rivers.push({id:rivers.length,points,canyonId:canyon.id});
  }
  const usedSrc: V3[] = [];
  const minSepDot = 1 - 0.05; // keep sources ≳18° apart so rivers spread out
  let attempts = 0;
  for (const cd of cands) {
    if (rivers.length >= target || attempts >= MAX_SOURCE_ATTEMPTS) break;
    let near = false;
    for (const u of usedSrc) if (cd.x * u.x + cd.y * u.y + cd.z * u.z > minSepDot) { near = true; break; }
    if (near) continue;
    attempts++;
    const path = traceRiver(sampler, cd, seaRel);
    if (path === null) continue;
    let poly = buildPolyline(sampler, path, rivers.length);
    // CONFLUENCE: if this river runs into an ALREADY-BUILT river's channel, it
    // becomes a TRIBUTARY — truncate it just past the junction and blend its
    // tail surface DOWN onto the host's level, so the two overlapping water
    // planes meet at one height instead of drawing a seam where they cross.
    // Earlier rivers (higher sources, longer runs) are the hosts; pure seed
    // order, so the junction graph is deterministic.
    if (rivers.length > 0) {
      const sofar = new RiverGraph(rivers, def.radius,nativeFalls);
      let cut = -1;
      let hostWs = 0;
      for (let i = 0; i < poly.points.length; i++) {
        const p = poly.points[i];
        const h = sofar.carveAt(p.x, p.y, p.z);
        if (h !== null && h.dist < h.waterHalf * 0.8) {
          cut = i;
          hostWs = h.ws;
          break;
        }
      }
      if (cut >= 0) {
        if (cut < MIN_POINTS) continue; // swallowed almost at the source — not a river
        poly.points.length = Math.min(poly.points.length, cut + 3); // overlap a little into the host
        const nn = poly.points.length;
        const blend = Math.min(6, nn);
        for (let i = nn - blend; i < nn; i++) {
          const k = (i - (nn - blend)) / Math.max(1, blend - 1);
          const p = poly.points[i];
          const eased = p.ws + (hostWs - p.ws) * k;
          if (eased < p.ws) {
            p.ws = eased; // downward only — a tributary never lifts to its host
            p.bed = p.ws - CHANNEL_DEPTH_M / sampler.amplitude;
          }
        }
      }
    }
    const fitted=fitNativeRiverCliffs(poly,def.radius,sampler.amplitude,seaRel,
      (x,y,z)=>sampler.drainageH(x,y,z),
      (x,y,z)=>sampler.glacierIceAt(x,y,z)>0||sampler.lavaFootprintAt(x,y,z));
    poly=fitted.river;
    // A trace step can straddle a narrow ridge, and a confluence can lower its
    // last reach. Validate the final interpolated bed before accepting either:
    // the bounded carve must leave continuous water, not dry mountain plugs.
    let fits = true;
    for (let i = 1; i < poly.points.length && fits; i++) {
      const a = poly.points[i-1], b = poly.points[i];
      if (b.ws > a.ws + 1e-12) { fits = false; break; }
      for (let j = 0; j <= 4; j++) {
        const t = j / 4;
        const d = norm({x:a.x+(b.x-a.x)*t,y:a.y+(b.y-a.y)*t,z:a.z+(b.z-a.z)*t});
        const s=a.s+(b.s-a.s)*t,fall=fitted.falls.find(f=>s>f.lip.s&&s<f.end.s);
        const bed = fall?fallBed(fall,s):a.bed+(b.bed-a.bed)*t;
        if(sampler.glacierIceAt(d.x,d.y,d.z)>0||sampler.lavaFootprintAt(d.x,d.y,d.z)) { fits=false; break; }
        if ((sampler.drainageH(d.x,d.y,d.z)-bed)*sampler.amplitude > (fall?RIVER_MAX_CUT_M-.5:6.5)) { fits = false; break; }
      }
    }
    // Both native banks must contain the water. An open downhill shoulder
    // cannot hold a standing channel; discard it instead of drawing a shelf.
    for (const p of poly.points) {
      if (!fits || p.ws <= seaRel) continue;
      const side = norm({x:p.fy*p.z-p.fz*p.y,y:p.fz*p.x-p.fx*p.z,z:p.fx*p.y-p.fy*p.x});
      for (const sign of [-1,1]) {
        const off = sign*p.half*BANK_FACTOR/def.radius;
        const d=norm({x:p.x+side.x*off,y:p.y+side.y*off,z:p.z+side.z*off});
        if(sampler.glacierIceAt(d.x,d.y,d.z)>0 || sampler.lavaFootprintAt(d.x,d.y,d.z) || sampler.drainageH(d.x,d.y,d.z)<p.ws) { fits=false; break; }
      }
    }
    if (!fits) continue;
    rivers.push(poly);
    nativeFalls.push(...fitted.falls);
    usedSrc.push(cd);
  }
  // Authored descending canyon floors and narrow streams share every segment;
  // the ordinary waterfall receiving-pool replacement would lower their water
  // without making the same deep pool in the already shaped canyon floor.
  const ordinaryFalls = buildRiverFalls(rivers.filter(r=>!r.canyonId), def.radius, sampler.amplitude, seaRel, (x,y,z) => sampler.drainageH(x,y,z));
  const falls = [...nativeFalls];
  for(const fall of ordinaryFalls){
    const same=falls.filter(f=>f.riverId===fall.riverId);
    if(same.length<2&&!same.some(f=>fall.lip.s<f.end.s+180&&fall.end.s>f.lip.s-180))falls.push(fall);
  }
  const landformsEnabled=def.atmosphere!==null&&def.hasFlora&&(def.archetype==='terran'||def.archetype==='ocean')&&def.terrain.amplitudeRel>=.001;
  const terrain:RiverPoolTerrain={amplitude:sampler.amplitude,nativeHeight:(x,y,z)=>sampler.drainageH(x,y,z),excluded:(x,y,z)=>sampler.glacierIceAt(x,y,z)>0||sampler.lavaFootprintAt(x,y,z)};
  const geology={}as TerrainSample;
  const cascadeTerrain={...terrain,excluded:(x:number,y:number,z:number)=>{
    if(terrain.excluded?.(x,y,z))return true;
    // Native specialist ground keeps authority across the entire amphitheatre.
    sampler.sample(x,y,z,geology,false,false,true,true);
    return (geology.canyonGround??0)>.01||(geology.canyonRock??0)>.01||(geology.butteRock??0)>.01||(geology.duneSand??0)>.08;
  }};
  const cascade=landformsEnabled?buildRiverEscarpment(rivers,falls,def.radius,seaRel,cascadeTerrain):{rivers,falls,features:[]};
  const scratch=escarpmentSample();
  const shapedTerrain=cascade.features.length?{...terrain,nativeHeight:(x:number,y:number,z:number)=>sampleRiverEscarpments(cascade.features,def.radius,sampler.amplitude,x,y,z,terrain.nativeHeight(x,y,z)*sampler.amplitude,scratch).heightM/sampler.amplitude}:terrain;
  return new RiverGraph(cascade.rivers,def.radius,cascade.falls,landformsEnabled?shapedTerrain:undefined,landformsEnabled,cascade.features);
}
